import { createSlice, nanoid, PayloadAction } from '@reduxjs/toolkit';

export type StopFormValues = {
  id: string;
  region: string | null;
  site: string | null;
  startDate: string | null;
  endDate: string | null;
  psVet: string | null;
  accountNumber: string;
  description: string;
  comments: string;
  enteredBy: string;
  status: 'tentative' | 'confirmed' | 'scheduled';
  opu: number | null;
  ftTx: number | null;
  fzTx: number | null;
  dfrs: number | null;
  pregs: number | null;
  flush: number | null;
};

export type TripFormValues = {
  id?: string;
  recurring: boolean;
  frequency: 'annual' | 'monthly' | 'bi-monthly' | 'weekly';
  shipmentNumber: string;
  psEmbryologist: string | null;
  psIntern: string | null;
  stops: StopFormValues[];
};

interface ModalState {
  open: boolean;
  mode: 'create' | 'edit';
  draft: TripFormValues | null;
}

const emptyStop = (): StopFormValues => ({
  id: nanoid(),
  region: null,
  site: null,
  startDate: null,
  endDate: null,
  psVet: null,
  accountNumber: '',
  description: '',
  comments: '',
  enteredBy: '',
  status: 'tentative',
  opu: null,
  ftTx: null,
  fzTx: null,
  dfrs: null,
  pregs: null,
  flush: null,
});

const initialState: ModalState = {
  open: false,
  mode: 'create',
  draft: null,
};

const createTripModalSlice = createSlice({
  name: 'createTripModal',
  initialState,
  reducers: {
    openForCreate(state) {
      state.open = true;
      state.mode = 'create';
      state.draft = {
        recurring: false,
        frequency: 'monthly',
        shipmentNumber: '',
        psEmbryologist: null,
        psIntern: null,
        stops: [emptyStop()],
      };
    },
    openForEdit(state, action: PayloadAction<TripFormValues>) {
      state.open = true;
      state.mode = 'edit';
      state.draft = action.payload;
    },
    closeModal(state) {
      state.open = false;
      state.draft = null;
    },
    patchDraft(state, action: PayloadAction<Partial<TripFormValues>>) {
      if (state.draft) Object.assign(state.draft, action.payload);
    },
    addStop(state) {
      state.draft?.stops.unshift(emptyStop());
    },
    updateStop: {
      reducer(
        state,
        action: PayloadAction<{ id: string; changes: Partial<StopFormValues> }>,
      ) {
        const stop = state.draft?.stops.find((s) => s.id === action.payload.id);
        if (stop) Object.assign(stop, action.payload.changes);
      },
      prepare(payload: { id: string; changes: Partial<StopFormValues> }) {
        const c = { ...payload.changes };
        ['startDate', 'endDate'].forEach((k) => {
          const key = k as 'startDate' | 'endDate';
          const val = c[key] as unknown;
          if (val instanceof Date) c[key] = val.toISOString();
        });
        return { payload: { id: payload.id, changes: c } };
      },
    },
    removeStop(state, action: PayloadAction<string>) {
      state.draft!.stops = state.draft!.stops.filter(
        (s) => s.id !== action.payload,
      );
    },
  },
});

export const {
  openForCreate,
  openForEdit,
  closeModal,
  patchDraft,
  addStop,
  updateStop,
  removeStop,
} = createTripModalSlice.actions;

export default createTripModalSlice.reducer;
